import { ChangeDetectionStrategy, Component, computed, DOCUMENT, inject, input, model, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ThemeListItem } from '@key-telematics/fleet-api-client';
import { AppService } from 'app/app.service';
import { KuiActionModule } from 'app/key-ui/action/action.module';
import { IKuiDropdownMenuItem, KuiDropdownModule } from 'app/key-ui/dropdown';
import { KuiIconModule } from 'app/key-ui/icon/icon.module';
import { LocalStore } from 'app/services';
import { CurrentClient, CurrentTheme } from 'app/shared/model';

// What the picker remembers between sessions, along with the client it was picked under so that
// changing client drops it and shows that client's configured theme. Absent means the client's
// configured theme applies.
const OVERRIDE_KEY = 'override';

// The last few themes picked, newest first, so that comparing a handful of themes doesn't mean
// finding each one in the full client list again.
const RECENTS_KEY = 'recents';
const RECENTS_LIMIT = 3;

interface ThemeOverride {
    themeId: string;
    clientId: string;
}

@Component({
    selector: 'key-theme-switcher',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [KuiActionModule, KuiDropdownModule, KuiIconModule],
    host: { '[class.menu-footer-action]': 'enabled()' },
    template: `
        @if (enabled()) {
            <kui-dropdown [style]="'none'" [menu]="menu()" maxHeight="70vh">
                <kui-icon class="ml-1" kui-action size="sm" name="theme" title="Theme"></kui-icon>
            </kui-dropdown>
        }
    `,
    styles: `
        :host {
            display: none;
        }

        // Matches the collapse arrow it sits beside, so the two read as one footer bar. The class is
        // in the selector to outrank the menu's '.app-menu * { border-width }' reset.
        :host(.menu-footer-action) {
            display: flex;
            align-items: center;

            border-top-style: solid;
            border-top-width: var(--width-border);

            padding: var(--padding);

            color: var(--footer-font-color);
            background-color: var(--footer-background-color);
            height: var(--height-header);
            padding-left: var(--navigation-padding);
        }
    `,
})
export class KeyThemeSwitcherComponent {

    private app = inject(AppService);
    private document = inject(DOCUMENT);
    private store = new LocalStore('theme-switcher');

    readonly collapsed = input(false);
    readonly enabled = computed(() => this.app.isSystemUser && this.app.isStaging && !this.collapsed());

    private client = signal<CurrentClient>(this.app.client);
    private themes = signal<ThemeListItem[]>([]);
    private override = signal<ThemeOverride>(this.store.get<ThemeOverride>(OVERRIDE_KEY));
    private recents = signal<string[]>(this.store.get<string[]>(RECENTS_KEY) || []);
    protected current = signal<CurrentTheme>(this.app.theme);

    /** Whether the theme editor panel is open. Two-way; the panel itself is mounted by the root. */
    readonly editing = model(false);

    readonly menu = computed<IKuiDropdownMenuItem[]>(() => {
        const items: IKuiDropdownMenuItem[] = [];

        const available = this.recents().map(id => this.themes().find(theme => theme.id === id)).filter(Boolean);
        if (available.length) {
            items.push(
                { type: 'heading', text: 'Recent' },
                ...available.map(item => this.buildThemeItem(item.id, item.name)),
                { type: 'divider' },
            );
        }

        items.push(
            {
                type: 'radio',
                text: `Client Theme (${this.getClientThemeName()})`,
                checked: !this.override(),
                closeDropdownOnClicked: true,
                action: () => this.selectDefault(),
            },
            { type: 'divider' },
            { type: 'heading', text: 'Available Themes' },
            ...this.themes().map(item => this.buildThemeItem(item.id, item.name)),
        );

        const theme = this.current();
        if (theme?.id && theme?.owner) {
            items.push(
                { type: 'divider' },
                {
                    type: 'action',
                    text: 'Edit...',
                    icon: 'edit',
                    closeDropdownOnClicked: true,
                    action: () => this.editing.set(true),
                },
            );
        }
        return items;
    });

    constructor() {
        if (this.app.isSystemUser && this.app.isStaging) {
            this.app.client$.pipe(takeUntilDestroyed()).subscribe(client => {
                this.client.set(client);
                if (client && this.override() && this.override().clientId !== client.id) {
                    this.setOverride(null);
                }
                if (client) {
                    this.app.api.entities.listThemes(client.id, true, 0, 100, 'name', 'state=active')
                        .then(result => this.themes.set(result.items))
                        .catch(() => this.themes.set([]));
                }
            });

            // an override outranks whatever else applies a theme, so that it survives the client
            // theme being applied on startup and on every client change
            this.app.theme$.pipe(takeUntilDestroyed()).subscribe(theme => {
                this.current.set(theme);
                if (this.override() && this.override().themeId !== theme.id) {
                    this.applyTheme(this.override().themeId);
                }
            });
        }
    }

    private getClientThemeName(): string {
        const theme = this.client()?.theme;
        if (theme?.id && theme.id !== 'inherited') {
            return theme.name;
        }
        return 'Inherited';
    }

    private buildThemeItem(id: string, name: string): IKuiDropdownMenuItem {
        return {
            type: 'radio',
            text: name,
            checked: this.override()?.themeId === id,
            closeDropdownOnClicked: true,
            action: () => this.selectTheme(id),
        };
    }

    private selectTheme(id: string): void {
        this.setOverride(id);
        this.recordRecent(id);
        this.applyTheme(id);
    }

    private recordRecent(id: string): void {
        const recents = [id, ...this.recents().filter(item => item !== id)].slice(0, RECENTS_LIMIT);
        this.store.set(RECENTS_KEY, recents);
        this.recents.set(recents);
    }

    private selectDefault(): void {
        this.setOverride(null);

        const client = this.client();
        if (client?.theme?.id && client.theme.id !== 'inherited') {
            this.applyTheme(client.theme.id, client.logoPath);
        } else {
            this.app.getDomainTheme(this.document.location.hostname);
        }
    }

    private setOverride(themeId: string): void {
        let override: ThemeOverride = null;
        if (themeId) {
            override = { themeId, clientId: this.client().id };
        }
        this.store.set(OVERRIDE_KEY, override);
        this.override.set(override);
    }

    private applyTheme(id: string, logoPath?: string): void {
        this.app.api.entities.getTheme(id)
            .then(theme => this.app.setTheme({ ...theme, logoPath }))
            .catch(() => this.setOverride(null)); // a theme this client cannot see must not keep fighting
    }

}
